import React, { useState, createContext, useContext } from 'react';
import styled from 'styled-components';

import { TabPanelProps, TabsProps, TabObject, ContextType, TabsContextProps, TabListProps } from './tabs.types';
import { default as ButtonGroupPattern } from '../ButtonGroup/buttonGroup';
import { ButtonShapes, ButtonStyles, ButtonSizes } from '../Button/button.types';

const fixTabName = (name: string) => name.replace(/ /g, '_').toLowerCase();
const Context = createContext<ContextType | null>(null);

const TabsContext = ({ initialActiveTab = '', children }: TabsContextProps) => {
  const [activeTab, setActiveTab] = useState(initialActiveTab);

  const tabProviderValue = {
    activeTab,
    setActiveTab,
  };

  return <Context.Provider value={tabProviderValue}>{children}</Context.Provider>;
};

const useTabsContext = () => {
  const context = useContext(Context);
  if (context === null) {
    throw new Error('useTabsContext must be used within a TabsContext Provider');
  }
  return context;
};

const ButtonGroup = styled(ButtonGroupPattern)`
  padding-bottom: 12px;
`;

const DEFAULT_DATA_ATTR = { 'data-testid': 'tab-list' };

const TabList = ({
  buttonProps = {},
  buttonSize = ButtonSizes.MEDIUM,
  buttonWrapperProps,
  dataAttr = {},
  list = [],
}: TabListProps) => {
  const tabContext = useTabsContext();

  const tabs = list.map((tabProps, i) => {
    const DEFAULT_BTN_DATA_ATTR = { 'data-testid': `tab-button--${i}` };
    const combinedDataAttr = {
      ...DEFAULT_BTN_DATA_ATTR,
      ...(tabProps.dataAttr ?? {}),
    };
    const handlers = {
      ...tabProps.handlers,
      onClick: (event: MouseEvent) => {
        if (tabContext.setActiveTab) {
          tabContext.setActiveTab(tabProps.name);
        }

        if (tabProps.handlers?.onClick) {
          tabProps.handlers.onClick(event);
        }
      },
    };

    return {
      ...tabProps,
      dataAttr: combinedDataAttr,
      handlers,
      hasHover: false,
      key: tabProps.name,
      label: tabProps.name,
      shape: ButtonShapes.TAB,
      size: buttonSize,
      ...buttonProps,
    };
  });
  const activeTabIndex = list.findIndex((tab) => tab.name === tabContext?.activeTab);
  const combinedDataAttr = { ...DEFAULT_DATA_ATTR, ...dataAttr };

  return (
    <ButtonGroup
      data={tabs}
      dataAttr={combinedDataAttr}
      buttonWrapperProps={buttonWrapperProps}
      selected={activeTabIndex}
      selectedStyleName={ButtonStyles.SELECTED_TAB}
    />
  );
};

const TabPanel = ({ name = '', children, ...restProps }: TabPanelProps) => {
  const tabContext = useTabsContext();
  return (tabContext && tabContext.activeTab) === name ? (
    <div id={`tabpanel_${fixTabName(name)}`} aria-labelledby={`tab_${fixTabName(name)}`} {...restProps}>
      {children}
    </div>
  ) : null;
};

const Tabs = ({
  buttonProps = {},
  buttonSize = ButtonSizes.MEDIUM,
  buttonWrapperProps,
  initialActiveTab = '',
  list = [],
  listDataAttr,
}: TabsProps) => (
  <TabsContext initialActiveTab={initialActiveTab}>
    <TabList
      buttonProps={buttonProps}
      buttonSize={buttonSize}
      buttonWrapperProps={buttonWrapperProps}
      dataAttr={listDataAttr}
      list={list}
    />
    {list.map((tab: TabObject) => (
      <TabPanel key={tab.name} name={tab.name}>
        {tab.children}
      </TabPanel>
    ))}
  </TabsContext>
);

export { TabPanel, Tabs, TabsContext, TabList, useTabsContext };
